import { del, get, post, put } from './apiClient';

import type { FormStatus } from '../types/permissionSlips';

export interface PermissionSlipFormSummary {
  id: number;
  name: string;
  title: string;
  description: string | null;
  form_code: string | null;
  status: FormStatus;
  category: string | null;
  is_template: boolean;
  created_at: string;
  updated_at: string;
}

export interface PermissionSlipFieldResponse {
  id: number;
  field_type: string;
  field_name: string;
  field_label: string;
  field_description: string | null;
  display_order: number;
  is_required: boolean;
  is_readonly: boolean;
  field_options: Array<Record<string, unknown>> | null;
  validation_rules: Record<string, unknown> | null;
  default_value: string | null;
}

export interface PermissionSlipFormDetail extends PermissionSlipFormSummary {
  fields: PermissionSlipFieldResponse[];
  submission_count: number;
  completion_rate: number | null;
}

export interface PermissionSlipFormsResponse {
  forms: PermissionSlipFormSummary[];
  total_count: number;
  limit: number;
  offset: number;
}

export interface CreatePermissionSlipFormRequest {
  name: string;
  title: string;
  district_id: number;
  description?: string;
  form_code?: string;
  school_id?: number;
  category?: string;
  activity_name?: string;
  activity_date?: string;
  activity_location?: string;
  activity_description?: string;
  cost?: number;
  requires_payment?: boolean;
  due_date?: string;
  allow_late_submissions?: boolean;
  requires_signature?: boolean;
  requires_witness?: boolean;
  multi_signature?: boolean;
  allow_digital_only?: boolean;
  allow_paper_fallback?: boolean;
}

export type UpdatePermissionSlipFormRequest = Partial<
  Omit<CreatePermissionSlipFormRequest, 'district_id'>
>;

export interface CreatePermissionSlipFieldRequest {
  field_type: string;
  field_name: string;
  field_label: string;
  field_description?: string;
  placeholder_text?: string;
  display_order?: number;
  is_required?: boolean;
  field_options?: Array<{ value: string; label: string; selected?: boolean }>;
  default_value?: string;
}

export interface DistributionResult {
  form_id: number;
  total_students: number;
  created_submissions: number;
  skipped_existing: number;
  errors: string[];
}

export interface DistributionAnalytics {
  form_id: number;
  total_submissions: number;
  status_breakdown: Record<string, number>;
  grade_breakdown: Record<string, number>;
  school_breakdown: Record<string, number>;
  completion_trend: Array<Record<string, unknown>>;
  completion_rate: number;
}

export interface DistributionDashboardSummary {
  active_forms: number;
  recent_submissions: number;
  total_submissions: number;
  overall_completion_rate: number;
  overdue_forms: number;
}

const withQuery = (
  path: string,
  params?: Record<string, string | number | boolean | undefined>
) => {
  const searchParams = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.set(key, String(value));
    }
  });
  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
};

export const createForm = (
  data: CreatePermissionSlipFormRequest
): Promise<PermissionSlipFormSummary> =>
  post<PermissionSlipFormSummary>('/api/permission-slip-forms/', data);

export const createFormFromTemplate = (
  templateId: number,
  data: CreatePermissionSlipFormRequest
): Promise<PermissionSlipFormSummary> =>
  post<PermissionSlipFormSummary>(
    `/api/permission-slip-forms/from-template/${templateId}`,
    data
  );

export const getForms = (params?: {
  offset?: number;
  limit?: number;
  status?: FormStatus;
  category?: string;
  is_template?: boolean;
  school_id?: number;
  search?: string;
}): Promise<PermissionSlipFormsResponse> =>
  get<PermissionSlipFormsResponse>(
    withQuery('/api/permission-slip-forms/', params)
  );

export const getForm = (
  formId: number
): Promise<PermissionSlipFormDetail> =>
  get<PermissionSlipFormDetail>(`/api/permission-slip-forms/${formId}`);

export const updateForm = (
  formId: number,
  data: UpdatePermissionSlipFormRequest
): Promise<PermissionSlipFormSummary> =>
  put<PermissionSlipFormSummary>(
    `/api/permission-slip-forms/${formId}`,
    data
  );

export const deleteForm = (formId: number): Promise<{ message: string }> =>
  del<{ message: string }>(`/api/permission-slip-forms/${formId}`);

export const activateForm = (
  formId: number
): Promise<PermissionSlipFormSummary> =>
  post<PermissionSlipFormSummary>(
    `/api/permission-slip-forms/${formId}/activate`
  );

export const addField = (
  formId: number,
  data: CreatePermissionSlipFieldRequest
): Promise<PermissionSlipFieldResponse> =>
  post<PermissionSlipFieldResponse>(
    `/api/permission-slip-forms/${formId}/fields`,
    data
  );

export const getFormTemplates = (
  category?: string
): Promise<PermissionSlipFormSummary[]> =>
  get<PermissionSlipFormSummary[]>(
    withQuery('/api/permission-slip-forms/templates/', { category })
  );

export const createTemplate = (
  formId: number,
  data: { name: string; title: string; description?: string; category?: string }
): Promise<PermissionSlipFormSummary> =>
  post<PermissionSlipFormSummary>(
    `/api/permission-slip-forms/${formId}/create-template`,
    data
  );

export const distributeToGradeLevels = (data: {
  form_id: number;
  grade_levels: string[];
  auto_populate?: boolean;
}): Promise<DistributionResult> =>
  post<DistributionResult>(
    '/api/permission-slip-distribution/grade-levels',
    data
  );

export const distributeToSchool = (data: {
  form_id: number;
  school_id: number;
  grade_levels?: string[];
  auto_populate?: boolean;
}): Promise<DistributionResult> =>
  post<DistributionResult>('/api/permission-slip-distribution/school', data);

export const distributeToStudents = (data: {
  form_id: number;
  student_ids: number[];
  auto_populate?: boolean;
}): Promise<DistributionResult> =>
  post<DistributionResult>('/api/permission-slip-distribution/students', data);

export const distributeToHomerooms = (data: {
  form_id: number;
  homeroom_teachers: string[];
  auto_populate?: boolean;
}): Promise<DistributionResult> =>
  post<DistributionResult>(
    '/api/permission-slip-distribution/homerooms',
    data
  );

export const getDistributionAnalytics = (
  formId: number
): Promise<DistributionAnalytics> =>
  get<DistributionAnalytics>(
    `/api/permission-slip-distribution/analytics/${formId}`
  );

export const getDistributionDashboardSummary =
  (): Promise<DistributionDashboardSummary> =>
    get<DistributionDashboardSummary>(
      '/api/permission-slip-distribution/dashboard/summary'
    );

export const permissionSlipApiService = {
  createForm,
  createFormFromTemplate,
  getForms,
  getForm,
  updateForm,
  deleteForm,
  activateForm,
  addField,
  getFormTemplates,
  createTemplate,
  distributeToGradeLevels,
  distributeToSchool,
  distributeToStudents,
  distributeToHomerooms,
  getDistributionAnalytics,
  getDistributionDashboardSummary,
};